import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import { useGoogleLogin } from '@react-oauth/google';
import toast from 'react-hot-toast';

import { setUser } from 'actions/userActions';
import { activeNetworkSelector, userSelector } from 'redux/selectors';
import {
  useCreateUserGoogleAccountMutation,
  useDeleteUserGoogleAccountMutation,
  useLazyGetUserGoogleAccountsQuery,
  useUpdateUserGoogleAccountMutation,
} from 'services/userGoogleAccountsV2';
import { useUpdateLinkedinSyncStatusMutation, useDisconnectLinkedinSyncMutation } from 'services/userV2';
import { useGoogleSync } from 'hooks/useGoogleSync';
import { useHasGoogleSyncFeature } from 'hooks/useHasGoogleSyncFeature';
import { useHasRelationshipStrengthFeature } from 'hooks/useHasRelationshipStrengthFeature';

// `useGoogleLogin` captures `scope` at hook-call time, so this constant
// exists to seed the modal-driven branch with the broadest read scope set
// the modal can produce; the actual scope is replaced via `setConnectScope`
// when the user submits the modal.
const DEFAULT_CONNECT_SCOPE =
  'openid email profile ' +
  'https://www.googleapis.com/auth/gmail.metadata ' +
  'https://www.googleapis.com/auth/calendar.events.readonly';

// Legacy contacts-only scope used when `relationship_strength` is off:
// the backend's `use_legacy_contacts_default?` path expects a code-only
// POST with People-API scopes granted, which keeps the row on the
// pre-GET-268 address-book sync surface.
const LEGACY_CONNECT_SCOPE =
  'openid email profile ' +
  'https://www.googleapis.com/auth/contacts.readonly ' +
  'https://www.googleapis.com/auth/contacts.other.readonly';

// FE copy of backend `OauthScopeBuilder::SCOPES_BY_TOGGLE` for email; calendar
// accepts either the new events.readonly or the pre-GET-615 calendar.readonly
// grant (any-of). Used to decide whether a preferences change can be a plain
// PATCH or must launch incremental OAuth to grant a missing scope.
const GATED_SCOPES_BY_TOGGLE = {
  email_sync_enabled: ['https://www.googleapis.com/auth/gmail.metadata'],
};
const CALENDAR_SCOPE_ALTERNATIVES = [
  'https://www.googleapis.com/auth/calendar.events.readonly',
  'https://www.googleapis.com/auth/calendar.readonly',
];

// Transient statuses the card polls through until the account settles.
// `calculating_relationships`: ingestion is done, Reachability is not.
const TRANSIENT_SYNC_STATUSES = ['syncing', 'disconnecting', 'calculating_relationships'];

// Mirrors the `onGoogleSuccess` POST mapping: enabling either UI toggle needs
// the email + calendar interaction stream, while contacts / relationship
// strength are their own persistence gates.
const preferencesToAttributes = (preferences) => ({
  email_sync_enabled: preferences.relationshipSyncEnabled || preferences.contactsSyncEnabled,
  calendar_sync_enabled: preferences.relationshipSyncEnabled || preferences.contactsSyncEnabled,
  contacts_sync_enabled: preferences.contactsSyncEnabled,
  relationship_strength_enabled: preferences.relationshipSyncEnabled,
  excluded_emails: preferences.excludedEmails,
  excluded_domains: preferences.excludedDomains,
});

export const useIntegrationsPage = ({ network: networkOverride, onGoogleSyncComplete } = {}) => {
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const activeNetworkFromStore = useSelector(activeNetworkSelector);
  const activeNetwork = networkOverride || activeNetworkFromStore;
  const hasGoogleSyncFeature = useHasGoogleSyncFeature({ network: activeNetwork });
  // A GoogleAccount and the OAuth grant behind it are per-user, not per-network:
  // one account feeds every network the user belongs to. Scoping this decision to
  // `activeNetwork` meant connecting from a flag-off network took the legacy
  // contacts-only grant, leaving the user's flag-on networks with no interaction
  // stream and no way to add one short of disconnecting. Resolve the feature
  // across the user's whole membership set instead.
  const hasRelationshipStrength = useHasRelationshipStrengthFeature();

  // LinkedIn sync
  const linkedinSync = useMemo(
    () => ({
      syncStatus: user.linkedinSync?.syncStatus || 'not_connected',
      lastSyncedAt: user.linkedinSync?.lastSyncedAt,
      contactsSynced: user.linkedinSync?.contactsSynced || 0,
    }),
    [user.linkedinSync],
  );
  const [updateLinkedinSyncStatus, { isLoading: isUpdatingLinkedinSync }] = useUpdateLinkedinSyncStatusMutation();
  const [disconnectLinkedinSync, { isLoading: isDisconnectingLinkedin }] = useDisconnectLinkedinSyncMutation();

  // Google sync
  const [createGoogleAccount, { isLoading: isCreatingGoogleAccount }] = useCreateUserGoogleAccountMutation();
  const [updateGoogleAccount, { isLoading: isUpdatingGoogleAccount }] = useUpdateUserGoogleAccountMutation();
  const [deleteGoogleAccount, { isLoading: isDeletingGoogleAccount }] = useDeleteUserGoogleAccountMutation();
  const { googleSync, isConnected: isGoogleConnected, refetch: refetchGoogleSync } = useGoogleSync();

  // Seeds the connect modal so a reconnect preserves the admin's existing
  // toggle and exclusion choices instead of resetting to all-on. `scopes` is
  // included so the modal can apply the same two-population gate as the card
  // when reconnecting: legacy contacts-only accounts (no walk scopes granted)
  // have `contactsSyncEnabled: false` in the column by default even though
  // they are still actively syncing contacts, and the granted scope is the
  // only reliable signal of "this sync is currently on".
  const initialConnectPreferences = useMemo(
    () =>
      isGoogleConnected
        ? {
            emailSyncEnabled: googleSync.emailSyncEnabled,
            calendarSyncEnabled: googleSync.calendarSyncEnabled,
            contactsSyncEnabled: googleSync.contactsSyncEnabled,
            relationshipStrengthEnabled: googleSync.relationshipStrengthEnabled,
            excludedEmails: googleSync.excludedEmails,
            excludedDomains: googleSync.excludedDomains,
            scopes: googleSync.scopes,
          }
        : null,
    [
      isGoogleConnected,
      googleSync.emailSyncEnabled,
      googleSync.calendarSyncEnabled,
      googleSync.contactsSyncEnabled,
      googleSync.relationshipStrengthEnabled,
      googleSync.excludedEmails,
      googleSync.excludedDomains,
      googleSync.scopes,
    ],
  );
  const [getGoogleAccountsStatus] = useLazyGetUserGoogleAccountsQuery();
  const pollingRef = useRef(null);

  // The connect modal holds the toggle + exclusion choices that flow into
  // both the OAuth scope string and the POST payload. We stash them in a
  // per-attempt map (see below) because the `useGoogleLogin` callback
  // closes over its hook config (including `scope` and `state`) at
  // hook-call time, so the popup URL must encode them BEFORE the user
  // gesture ends.
  const [googleConnectModalOpen, setGoogleConnectModalOpen] = useState(false);
  const [connectScope, setConnectScope] = useState(DEFAULT_CONNECT_SCOPE);
  // Each modal-driven submit gets its own monotonic `attemptId`. The id is
  // forwarded into the OAuth `state` parameter and round-tripped by Google
  // — that lets each `onSuccess`/`onError` invocation identify which
  // launch it belongs to. `useGoogleLogin` reuses a single
  // `onSuccess`/`onError` closure for every popup it opens, so without
  // per-launch tagging a stale popup A could consume — or clear — popup
  // B's prefs from a shared ref. Per-attempt prefs (`pendingPrefsByAttemptRef`)
  // are looked up by the `state` echoed back from Google; absent or
  // unknown ids are dropped (cancelled / stale) except for the legacy
  // direct-connect path (state === '') which intentionally POSTs
  // `preferences: undefined` so the backend takes its address-book route.
  const pendingPrefsByAttemptRef = useRef(new Map());
  const attemptCounterRef = useRef(0);
  const activeAttemptIdRef = useRef(null);
  // Producer-side guard against duplicate launches: even if a caller bypasses
  // the modal's button disable (programmatic re-submit, fast direct invoke),
  // we must not open two consent popups for one connect intent. The ref
  // mirrors `isLaunchingGoogleOAuth` for synchronous reads inside the same
  // event-loop turn — React state updates are async, so reading the boolean
  // directly here would still let two back-to-back calls slip through.
  //
  // Hoisted here (above `consumeAttemptByState` and the OAuth callbacks)
  // because those handlers fire asynchronously today but the `const` slot
  // would TDZ-throw under any future synchronous refactor.
  const isLaunchingRef = useRef(false);
  // Mirrors the attempt id into OAuth `state` so popup callbacks can
  // identify themselves. Empty string == legacy direct-connect path —
  // when the flag is off the card calls `googleLogin()` without going
  // through `submitGoogleConnectModal`, so there is no attempt to track.
  const [connectState, setConnectState] = useState('');
  // OAuth runs asynchronously from the modal's POST mutation, so the submit
  // button must reflect "OAuth popup in flight" too. Otherwise a fast double-
  // click bumps `launchEpoch` twice and the launch-effect opens two consent
  // popups for one connect intent.
  const [isLaunchingGoogleOAuth, setIsLaunchingGoogleOAuth] = useState(false);

  // The `/me` copy of the accounts (Redux `user.googleSyncAccounts`) drives the
  // intro-path nudge, while the card reads the RTK `google-accounts` cache. Only
  // the RTK tag is invalidated on connect/disconnect, so mirror the change into
  // Redux here to keep both surfaces consistent without a full page reload.
  // Declared above the polling effect because that effect removes accounts from
  // Redux once a disconnect settles.
  const upsertGoogleAccountInUser = useCallback(
    (account) => {
      if (!account?.id) return;
      const existing = user.googleSyncAccounts || [];
      const merged = existing.some((a) => a.id === account.id)
        ? existing.map((a) => (a.id === account.id ? account : a))
        : [...existing, account];
      dispatch(setUser({ ...user, googleSyncAccounts: merged }));
    },
    [dispatch, user],
  );

  const removeGoogleAccountFromUser = useCallback(
    (id) => {
      const remaining = (user.googleSyncAccounts || []).filter((account) => account.id !== id);
      dispatch(setUser({ ...user, googleSyncAccounts: remaining }));
    },
    [dispatch, user],
  );

  useEffect(() => {
    let pendingQuery = null;

    const cleanup = () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pendingQuery?.abort();
    };

    // `disconnecting` settles by the account being purged (it disappears),
    // `syncing` by reaching a terminal sync state; refetch once either does.
    const isDisconnecting = googleSync.syncStatus === 'disconnecting';
    if (!TRANSIENT_SYNC_STATUSES.includes(googleSync.syncStatus)) {
      cleanup();
      return () => {};
    }

    pollingRef.current = setInterval(() => {
      pendingQuery = getGoogleAccountsStatus();
      pendingQuery.then((res) => {
        const accounts = res?.data || [];
        const account = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : null;
        // A missing account only settles a disconnect. During `syncing` a
        // transient empty/error response must keep polling, not stop early.
        const settled = account ? !TRANSIENT_SYNC_STATUSES.includes(account.syncStatus) : isDisconnecting;
        if (settled) {
          // Disconnect completes by the account being purged; drop it from
          // Redux only now so `/me`-driven surfaces flip in lockstep with the
          // card rather than the moment the delete was requested.
          if (isDisconnecting && !account && googleSync.id) {
            removeGoogleAccountFromUser(googleSync.id);
          }
          refetchGoogleSync();
          cleanup();
        }
      });
    }, 3000);

    return cleanup;
  }, [googleSync.syncStatus, googleSync.id, getGoogleAccountsStatus, refetchGoogleSync, removeGoogleAccountFromUser]);

  // Looks up the prefs for a callback's originating launch. The OAuth
  // response echoes back the `state` we set on the popup config; matching
  // it against the per-attempt prefs map is what lets us distinguish:
  //  - state === '' → legacy direct-connect; intentionally process with no
  //    prefs so the backend routes to the address-book pipeline.
  //  - state present and in the map → modal-driven attempt; consume.
  //  - state present but missing from the map → already cancelled, already
  //    consumed, or superseded; drop without touching shared state.
  // We only mutate `isLaunching*` for the active attempt id so a stale
  // popup that fires late cannot clear an in-flight newer attempt's
  // submit-button disabled state.
  const consumeAttemptByState = (state) => {
    if (!state) {
      return { drop: false, preferences: null, attemptId: null };
    }
    const tagged = pendingPrefsByAttemptRef.current.get(state);
    if (!tagged) {
      return { drop: true, preferences: null, attemptId: state };
    }
    pendingPrefsByAttemptRef.current.delete(state);
    if (activeAttemptIdRef.current === state) {
      activeAttemptIdRef.current = null;
      isLaunchingRef.current = false;
      setIsLaunchingGoogleOAuth(false);
      // Returning to the empty state means a later direct `googleLogin()`
      // (legacy flag-off connect from the card) won't echo a consumed
      // attemptId back into `consumeAttemptByState`, which would otherwise
      // drop the legacy callback via the "missing-map" branch.
      setConnectState('');
    }
    return { drop: false, preferences: tagged, attemptId: state };
  };

  const onGoogleSuccess = async ({ code, state }) => {
    const { drop, preferences } = consumeAttemptByState(state);
    if (drop) return;
    // Legacy direct-connect (state === '') also needs to clear the
    // in-flight flag — there is no attempt entry to consume, but the
    // global launching guards may have been set if the caller routes
    // through the hook's `googleLogin` directly.
    if (!state && isLaunchingRef.current) {
      isLaunchingRef.current = false;
      setIsLaunchingGoogleOAuth(false);
    }
    try {
      const account = await createGoogleAccount({
        code,
        // Two UI toggles collapse into four backend booleans: both relationship
        // strength and contacts sync need the email/calendar read stream
        // (hence the OR onto `*_sync_enabled`), while `relationship_strength_enabled`
        // is a separate write-side gate on InteractionEvent persistence.
        preferences: preferences
          ? {
              email_sync_enabled: preferences.relationshipSyncEnabled || preferences.contactsSyncEnabled,
              calendar_sync_enabled: preferences.relationshipSyncEnabled || preferences.contactsSyncEnabled,
              contacts_sync_enabled: preferences.contactsSyncEnabled,
              relationship_strength_enabled: preferences.relationshipSyncEnabled,
              excluded_emails: preferences.excludedEmails,
              excluded_domains: preferences.excludedDomains,
            }
          : undefined,
      }).unwrap();
      upsertGoogleAccountInUser(account);
      toast('Settings updated', {
        icon: 'success',
        id: 'google-account-connected',
      });
      setGoogleConnectModalOpen(false);
      onGoogleSyncComplete?.();
    } catch (error) {
      const message = error?.data?.error || 'Failed to connect Google account';
      toast(message, { icon: 'error' });
    }
  };

  const onGoogleError = (error) => {
    // `@react-oauth/google` emits onError without `state` when the popup is
    // closed via the browser chrome (no auth-server roundtrip). Falling back
    // to `activeAttemptIdRef.current` lets `consumeAttemptByState` still
    // identify the attempt that was in flight so its entry in the per-attempt
    // prefs map (and `activeAttemptIdRef`) gets cleared. Without this, a
    // late onSuccess for the same attempt id (rare browser race) could find
    // the abandoned prefs and POST them with a fresh code.
    const fallbackState = error?.state || activeAttemptIdRef.current || '';
    // Stale popups belonging to a cancelled or superseded attempt are dropped
    // before any shared state is touched. That prevents a late `onError`
    // from clearing a fresher attempt's pending prefs or in-flight flag.
    const { drop } = consumeAttemptByState(fallbackState);
    if (drop) return;
    if (!fallbackState && isLaunchingRef.current) {
      isLaunchingRef.current = false;
      setIsLaunchingGoogleOAuth(false);
    }

    let message = 'Failed to start Google authorization';

    if (error?.error === 'access_denied') {
      message =
        'Google authorization was cancelled. Please try again and grant the required permissions to sync your contacts.';
    } else if (error?.error) {
      message = error.error;
    }

    toast(message, { icon: 'error' });
  };

  // When the user has no relationship-strength network at all, the connect path
  // bypasses the modal and asks Google directly for the People-API contacts scopes — the
  // backend then routes the row through its legacy address-book pipeline.
  const effectiveConnectScope = hasRelationshipStrength ? connectScope : LEGACY_CONNECT_SCOPE;
  // Defence-in-depth: the legacy flag-off path never goes through
  // `submitGoogleConnectModal`, so it has no attemptId to track. Force the
  // state empty when the flag is off even if `connectState` somehow
  // carries a stale modal attemptId — that keeps callbacks routed to the
  // legacy "no-prefs" branch instead of the drop branch.
  const effectiveConnectState = hasRelationshipStrength ? connectState : '';
  const googleLogin = useGoogleLogin({
    onSuccess: onGoogleSuccess,
    onError: onGoogleError,
    flow: 'auth-code',
    redirect_uri: 'postmessage',
    scope: effectiveConnectScope,
    // Round-trip an opaque attempt id through Google so callbacks can
    // identify which popup they belong to. See `pendingPrefsByAttemptRef`
    // above for the full rationale.
    state: effectiveConnectState,
    // Preserve previously granted Google scopes on reconnect (incremental
    // authorization). Without this, reconnecting with a different scope set
    // can revoke existing grants and force the backend into a
    // `permissions_missing` state.
    include_granted_scopes: true,
  });
  // `useGoogleLogin` returns a new closure on every render — the closure
  // captures the scope/state from its render. We mirror the latest closure
  // into a ref so the synchronous launch path in `submitGoogleConnectModal`
  // can invoke the freshly-rendered closure (with the just-flushed
  // scope/state) without depending on the captured-in-useCallback identity.
  const googleLoginRef = useRef(googleLogin);
  googleLoginRef.current = googleLogin;

  const openGoogleConnectModal = useCallback(() => {
    setGoogleConnectModalOpen(true);
  }, []);

  // Cancelling the modal removes the active attempt's prefs from the
  // per-attempt map — popup A returning later finds nothing under its own
  // state id and falls into the drop branch. We do NOT touch the entries
  // for OTHER attempts in the map; that is what makes the resubmit-while-
  // popup-A-pending case safe (popup A drops, popup B's entry stays). We
  // also reset `connectState` so a later legacy direct-connect doesn't
  // echo a stale attemptId back into `consumeAttemptByState`.
  const closeGoogleConnectModal = useCallback(() => {
    setGoogleConnectModalOpen(false);
    const cancelled = activeAttemptIdRef.current;
    if (cancelled !== null) {
      pendingPrefsByAttemptRef.current.delete(cancelled);
      activeAttemptIdRef.current = null;
    }
    isLaunchingRef.current = false;
    setIsLaunchingGoogleOAuth(false);
    setConnectState('');
  }, []);

  // The OAuth popup MUST be opened inside the user-gesture turn that
  // produced the click. If we deferred the launch into a `useEffect`,
  // browsers (Safari especially) would lose the gesture across the React
  // render and block the popup. We `flushSync` the scope/state updates so
  // the `useGoogleLogin` hook re-runs with the new config and writes the
  // fresh closure into `googleLoginRef`, then call that closure
  // synchronously — the OAuth URL is built with the new scope and state
  // while the click is still considered active.
  const submitGoogleConnectModal = useCallback((preferences) => {
    if (isLaunchingRef.current) return;
    isLaunchingRef.current = true;
    attemptCounterRef.current += 1;
    const attemptId = String(attemptCounterRef.current);
    activeAttemptIdRef.current = attemptId;
    pendingPrefsByAttemptRef.current.set(attemptId, preferences);
    flushSync(() => {
      setConnectScope(preferences.scope);
      setConnectState(attemptId);
      setIsLaunchingGoogleOAuth(true);
    });
    googleLoginRef.current();
  }, []);

  const patchGoogleSyncPreferences = useCallback(
    async (id, attributes, preferences) => {
      try {
        const account = await updateGoogleAccount({ id, ...attributes }).unwrap();
        upsertGoogleAccountInUser(account);
        refetchGoogleSync();
        toast('Sync preferences updated', { icon: 'success', id: 'google-account-updated' });
        setGoogleConnectModalOpen(false);
      } catch (error) {
        // FE/BE scope-map drift: a rejected scope means the account never
        // granted it, so fall back to incremental OAuth instead of a raw toast.
        if (error?.status === 422 && error?.data?.error === 'Required OAuth scope not granted') {
          submitGoogleConnectModal(preferences);
          return;
        }
        const message = error?.data?.error || 'Failed to update Google sync preferences';
        toast(message, { icon: 'error' });
      }
    },
    [updateGoogleAccount, upsertGoogleAccountInUser, refetchGoogleSync, submitGoogleConnectModal],
  );

  // Preferences entrypoint for an already-connected account: PATCH when every
  // enabled toggle's scope is already granted, otherwise launch incremental
  // OAuth to grant the missing scope (never a disconnect + reconnect). First
  // connect has no account yet, so it always goes through OAuth.
  const submitGooglePreferences = useCallback(
    (preferences) => {
      if (!isGoogleConnected || !googleSync?.id) {
        submitGoogleConnectModal(preferences);
        return;
      }
      const attributes = preferencesToAttributes(preferences);
      const grantedScopes = googleSync.scopes || [];
      const emailNeedsConsent =
        attributes.email_sync_enabled === true &&
        GATED_SCOPES_BY_TOGGLE.email_sync_enabled.some((scope) => !grantedScopes.includes(scope));
      const calendarNeedsConsent =
        attributes.calendar_sync_enabled === true &&
        !CALENDAR_SCOPE_ALTERNATIVES.some((scope) => grantedScopes.includes(scope));
      const needsConsent = emailNeedsConsent || calendarNeedsConsent;
      if (needsConsent) {
        submitGoogleConnectModal(preferences);
        return;
      }
      patchGoogleSyncPreferences(googleSync.id, attributes, preferences);
    },
    [isGoogleConnected, googleSync?.id, googleSync.scopes, submitGoogleConnectModal, patchGoogleSyncPreferences],
  );

  const getLinkedinSyncLink = useCallback(
    (status = 'sync') =>
      user.isOnlyAlly || !activeNetwork?.slug
        ? `/linkedin/${status}`
        : `/networks/${activeNetwork.slug}/linkedin/${status}`,
    [activeNetwork?.slug, user.isOnlyAlly],
  );

  // Anchor the "My networks" CTA to whichever Settings tree the user is
  // currently in (network-scoped vs ally-scoped) instead of hard-coding the
  // network slug, so the link stays consistent with the page they navigated
  // from and matches the pattern used by myNetworks/index.jsx.
  const settingsBaseMatch = useRouteMatch(['/networks/:networkSlug/settings', '/settings']);
  const myNetworksLink = `${(settingsBaseMatch?.url || '/settings').replace(/\/$/, '')}/my-networks`;

  // Treat "any integration is connected" as true when LinkedIn is wired up OR
  // (only when the google_sync feature is on for this network) when Google is
  // wired up. Mirrors contactsExtended/importButton so the page-level warning
  // and the in-table CTA agree on what "connected" means — read the raw redux
  // `user.linkedinSync` here (importButton does the same) rather than the
  // hook's normalized `linkedinSync`, which is always a truthy object.
  const isLinkedinConnected = Boolean(user.linkedinSync);
  const isAnyConnected = isLinkedinConnected || (hasGoogleSyncFeature && isGoogleConnected);

  // Gate on activeNetwork being present so the warning never flashes during
  // the brief window before the network store is hydrated (when activeNetwork
  // is null but `useHasGoogleSyncFeature()` can still fall back to the user's
  // collections and report true).
  const shouldShowShareContactsWarning = Boolean(activeNetwork) && isAnyConnected && !activeNetwork.shareContacts;

  const handleConfirmRemoveLinkedin = useCallback(async () => {
    try {
      await disconnectLinkedinSync().unwrap();
      toast('Settings updated', {
        icon: 'success',
        id: 'linkedin-sync-disconnected',
      });
    } catch (error) {
      const message = error?.data?.error || 'Failed to disconnect LinkedIn sync';
      toast(message, { icon: 'error' });
    }
  }, [disconnectLinkedinSync]);

  const handleConfirmRemoveGoogle = useCallback(async () => {
    try {
      if (!googleSync?.id) return;
      await deleteGoogleAccount({ id: googleSync.id }).unwrap();
      // The account is kept in Redux until the disconnect poll confirms it was
      // purged (see the polling effect); removing it here would flash the
      // intro-path connect nudge while the card still shows `disconnecting`.
      toast('Settings updated', {
        icon: 'success',
        id: 'google-account-removed',
      });
    } catch (error) {
      const message = error?.data?.error || 'Failed to remove Google account';
      toast(message, { icon: 'error' });
    }
  }, [deleteGoogleAccount, googleSync?.id]);

  return {
    user,
    activeNetwork,
    googleSync,
    googleLogin,
    googleConnectModalOpen,
    initialConnectPreferences,
    openGoogleConnectModal,
    closeGoogleConnectModal,
    submitGoogleConnectModal,
    isCreatingGoogleAccount,
    isLaunchingGoogleOAuth,
    isUpdatingGoogleAccount,
    submitGooglePreferences,
    getLinkedinSyncLink,
    handleConfirmRemoveGoogle,
    handleConfirmRemoveLinkedin,
    hasGoogleSyncFeature,
    hasRelationshipStrength,
    isDisconnectingLinkedin,
    isDeletingGoogleAccount,
    linkedinSync,
    isUpdatingLinkedinSync,
    updateLinkedinSyncStatus,
    isAnyConnected,
    myNetworksLink,
    shouldShowShareContactsWarning,
  };
};
